<template>
    <div class="r1s1form">
        <div class="inline-desktop departure-arrival">
            <div class="own-input">
                <label for="departure">{{ $t('f_departure_label') }}</label>
                <info-tip :text="$t('f_departure_tip_text')" />

                <autocomplete
                    id="departure_airport"
                    v-model="departure_airport"
                    :limit="$device.mobile() ? 3 : 5"
                    :placeholder="$t('f_dep_airports_placeholder')"
                    url="/airports"
                    :data-vv-scope="scope"
                    field-to-show="label" />
            </div>

            <div class="own-input">
                <label for="arrival">{{ $t('f_arrival_label') }}</label>
                <info-tip class="right-content" :text="$t('f_arrival_tip_text')" />

                <autocomplete
                    id="arrival_airport"
                    v-model="arrival_airport"
                    :limit="$device.mobile() ? 3 : 5"
                    :placeholder="$t('f_arr_airports_placeholder')"
                    url="/airports"
                    :data-vv-scope="scope"
                    field-to-show="label" />
            </div>
        </div>

        <div class="own-input plane-was">
            <label>{{ $t('f_kind_label') }}</label>
            <info-tip :text="$t('f_kind_field_tooltip')" />
            <div v-if="$device.mobile()" class="select-wrap">
                <span class="icon icon-chevron-up" />
                <select
                    id="kind"
                    v-model="kind"
                    v-validate="'required'"
                    v-m-android-focus
                    name="kind"
                    :data-vv-scope="scope">
                    <option value="">
                        {{ $t('f_kind_placeholder') }}
                    </option>
                    <option v-for="(title, value) of kindMapper" :key="value" :value="value">
                        {{ title }}
                    </option>
                </select>
            </div>

            <div v-else class="inline-desktop inline-input">
                <div class="own-input">
                    <div class="check-btn">
                        <input
                            id="kind-delay"
                            v-model="kind"
                            v-validate="'required'"
                            :name="`kind${_uid}`"
                            type="radio"
                            value="delayed"
                            :data-vv-scope="scope">
                        <label for="kind-delay">
                            {{ kindMapper['delayed'] }}
                        </label>
                    </div>
                </div>
                <div class="own-input">
                    <div class="check-btn">
                        <input
                            id="kind-cancel"
                            v-model="kind"
                            :name="`kind${_uid}`"
                            type="radio"
                            value="cancelled">
                        <label for="kind-cancel">
                            {{ kindMapper['cancelled'] }}
                        </label>
                    </div>
                </div>
                <div class="own-input">
                    <div class="check-btn">
                        <input
                            id="kind-overbook"
                            v-model="kind"
                            :name="`kind${_uid}`"
                            type="radio"
                            value="overbooked">
                        <label for="kind-overbook">
                            {{ kindMapper['overbooked'] }}
                        </label>
                    </div>
                </div>
                <div class="own-input">
                    <div class="check-btn">
                        <input
                            id="kind-early"
                            v-model="kind"
                            :name="`kind${_uid}`"
                            type="radio"
                            value="early">
                        <label for="kind-early">
                            {{ kindMapper['early'] }}
                        </label>
                    </div>
                </div>
                <div class="own-input">
                    <div class="check-btn">
                        <input
                            id="kind-corona"
                            v-model="kind"
                            :name="`kind${_uid}`"
                            type="radio"
                            value="corona">
                        <label for="kind-corona">
                            {{ kindMapper['corona'] }}
                        </label>
                    </div>
                </div>
            </div>
            <span class="error-message">{{ errors.first(`${scope}.kind${_uid}`) }}</span>
        </div>

        <div v-if="selectedKindEarly" class="own-input">
            <label> {{ $t('f_early_flight_button') }}</label>
            <info-tip :text="$t('f_early_flight_field_label')" />
            <div class="select-wrap">
                <span class="icon icon-chevron-up" />
                <select
                    id="clientInformed"
                    v-model="client_informed"
                    v-validate="'required'"
                    v-m-android-focus
                    name="clientInformed"
                    :data-vv-scope="scope"
                    @change="setUncompensableDelay">
                    <option value="">
                        {{ $t('f_delay_placeholder') }}
                    </option>
                    <option v-for="(title, value) of informedEarlyFlightsMapper" :key="value" :value="value">
                        {{ title }}
                    </option>
                    <optgroup label="" />
                </select>
            </div>
        </div>

        <div v-if="selectedKindEarlyAndCompensable" class="own-input">
            <label for="delay">{{ $t('f_by_how_many_hours_departure_moved') }}</label>
            <div class="select-wrap">
                <span class="icon icon-chevron-up" />
                <select
                    id="delay"
                    key="early-delay"
                    v-model="delay"
                    v-validate="{required: selectedKindEarlyAndCompensable}"
                    v-m-android-focus
                    name="delay"
                    :data-vv-scope="scope">
                    <option value="">
                        {{ $t('f_delay_placeholder') }}
                    </option>
                    <option
                        v-for="(title, value) of delaysEarly714DaysMapper"
                        v-if="selectedEarlyInformedLessThan14DaysBefore"
                        :key="value"
                        :value="value">
                        {{ title }}
                    </option>
                    <option
                        v-for="(title, value) of delaysEarly7DaysMapper"
                        v-if="selectedEarlyInformedLessThan7DaysBefore"
                        :key="value"
                        :value="value">
                        {{ title }}
                    </option>
                    <optgroup label="" />
                </select>
            </div>
            <span class="error-message">{{ errors.first(`${scope}.delay`) }}</span>
        </div>

        <div v-if="selectedKindNotEarly" class="own-input">
            <label for="delay">{{ $t('f_delay_label') }}</label>
            <info-tip :text="$t('f_delay_tip_text')" />
            <div class="select-wrap">
                <span class="icon icon-chevron-up" />
                <select
                    id="delay"
                    key="regular-delay"
                    v-model="delay"
                    v-validate="{required: selectedKindNotEarly}"
                    v-m-android-focus
                    name="delay"
                    :data-vv-scope="scope"
                    @change="delayKindChanged">
                    <option value="">
                        {{ $t('f_delay_placeholder') }}
                    </option>
                    <option
                        v-for="(title, value) of delaysMapperWithCorona"
                        v-if="selectedKindCorona"
                        :key="value"
                        :value="value">
                        {{ title }}
                    </option>
                    <option
                        v-for="(title, value) of delaysMapperForRegular"
                        v-if="selectedKindRegular"
                        :key="value"
                        :value="value">
                        {{ title }}
                    </option>
                    <option
                        v-for="(title, value) of delaysMapperForDelayedWithMissedConnection"
                        v-if="delaysMapperDelayedWithMissedConnection"
                        :key="value"
                        :value="value">
                        {{ title }}
                    </option>
                    <optgroup label="" />
                </select>
            </div>
            <span class="error-message">{{ errors.first(`${scope}.delay`) }}</span>
        </div>

        <div v-show="reasonVisibility" class="own-input">
            <label for="reason">{{ $t('f_reason_label') }}</label>
            <info-tip :text="$t('f_reason_tip_text')" />
            <div class="select-wrap">
                <span class="icon icon-chevron-up" />
                <select
                    id="reason"
                    v-model="reason"
                    v-validate="'required'"
                    v-m-android-focus
                    name="reason"
                    :data-vv-scope="scope">
                    <option
                        v-for="(reason, i) in reasonsMapper"
                        :key="`reason-${i}`"
                        :value="reason['value']">
                        {{ reason['title'] }}
                    </option>
                </select>
            </div>
            <span class="error-message">{{ errors.first(`${scope}.reason`) }}</span>
        </div>

        <div class="own-input people">
            <label>{{ $t('f_travelers_count_label') }}</label>
            <info-tip :text="$t('f_travelers_count_tip_text')" />
            <count-input
                v-model="people"
                :decrement="decrementPeople"
                :increment="incrementPeople"
                readonly
                :text="''" />
        </div>

        <div class="own-input date">
            <label for="scheduled-arrival-date">{{ $t('f_scheduled_arrival_date_label') }}</label>
            <info-tip :text="$t('f_scheduled_arrival_date_tip_text')" />
            <div class="icon icon-calendar" />
            <calendar
                id="scheduled-arrival-date"
                v-model="scheduled_arrival_ui_date"
                v-validate="'required'"
                v-m-android-focus
                name="scheduled_arrival_ui_date"
                :format="date_format"
                :format-date="formatDate"
                :input-attributes="{readonly: true}"
                :is-date-disabled="isFutureDate"
                :parse-date="parseDate"
                :selectable-year-range="15"
                :data-vv-scope="scope"
                :class="{'invalid': errors.first(`${scope}.scheduled_arrival_ui_date`)}" />
            <span class="error-message">{{ errors.first(`${scope}.scheduled_arrival_ui_date`) }}</span>
        </div>


        <div class="own-input">
            <span>
                <label for="flightNumber">{{ $t('f_flight_number_label') }}</label>
                <info-tip :text="$t('f_flight_number_tip_text')" />
                <input
                    id="flightNumber"
                    v-model="flight_number"
                    v-validate="'required'"
                    v-m-android-focus
                    name="flightNumber"
                    type="text"
                    :data-vv-scope="scope"
                    maxlength="15">
            </span>
            <span class="error-message">{{ errors.first(`${scope}.flightNumber`) }}</span>
            <span v-if="isFlightNumberWarn" class="warning-message">
                {{ $t('f_v_flight_number_warning') }}
            </span>
        </div>

        <div v-show="showMissedConnectionFlightInput" class="own-input">
            <span>
                <label for="nextFlightNumber">{{ $t('f_next_flight_number_label') }}</label>
                <info-tip :text="$t('f_next_flight_number_tip_text')" />

                <input
                    id="flightNumberMissedConnection"
                    v-model="next_flight_number"
                    v-validate="{required: showMissedConnectionFlightInput}"
                    v-m-android-focus
                    name="flightNumberMissedConnection"
                    type="text"
                    :data-vv-scope="scope"
                    maxlength="15">
            </span>
            <span class="error-message">{{ errors.first(`${scope}.nextFlightNumber`) }}</span>
            <span v-if="isFlightNumberMissedConnectionWarn" class="warning-message">
                {{ $t('f_v_flight_number_warning') }}
            </span>
        </div>

        <div class="own-input message-label-wrap">
            <div class="label">
                <label for="is_comment" v-html="$t('f_comment_label')" />
                <info-tip :text="$t('f_comment_tip_text')" />
            </div>
            <label class="switch">
                <input
                    id="is_comment"
                    v-model="is_comment"
                    type="checkbox"
                    checked>
                <span class="slider round" />
            </label>
        </div>

        <div v-show="is_comment" class="own-input message-wrap">
            <textarea
                id="comment"
                v-model="comment"
                v-m-android-focus
                cols="5"
                rows="5" />
        </div>

    
        <server-errors v-if="serversErrors" :message="serversErrors" />
        <div v-show="editable" class="own-input next-prev-wrap">
            <div class="btn btn-link prev" @click="cancelEditingWrap()">
                {{ $t('f_cancel') }}
            </div>
            <div :class="['btn btn-orange', {'invalid': formIsInvalid(scope), 'progress': loading}]" @click="save">
                <span v-show="!loading">{{ $t('f_save') }} &#10093;</span>
                <div v-if="loading" class="spinner">
                    <div class="rect1" />
                    <div class="rect2" />
                    <div class="rect3" />
                    <div class="rect4" />
                    <div class="rect5" />
                </div>
            </div>
        </div>

        <div v-show="!editable" class="own-input next-prev-wrap thin">
            <div :class="['btn btn-orange', {'progress': loading}]" @click="next">
                <span v-show="!loading">{{ $t('f_continue') }} &#10093;</span>
                <div v-if="loading" class="spinner">
                    <div class="rect1" />
                    <div class="rect2" />
                    <div class="rect3" />
                    <div class="rect4" />
                    <div class="rect5" />
                </div>
            </div>
        </div>

        <div style="clear: both" />
    </div>
</template>

<script>
  import {mapGetters, mapMutations, mapActions} from 'vuex'
  import {createHelpers} from 'vuex-map-fields'
  import Autocomplete from '../common/Autocomplete'
  import CountInput from '../common/CountInput'
  import formNavMixin from '../mixins/formNavMixin'
  import Calendar from '../common/Calendar'
  import moment from 'moment'
  import { transliterate as tr } from 'transliteration';

  const { mapFields } = createHelpers({
    getterType: 'round1/getComplaintField',
    mutationType: 'round1/UPDATE_COMPLAINT_FIELD'
  })

  export default {
    components: { Autocomplete, CountInput, Calendar },
    mixins: [formNavMixin],
    inject: ['$validator'],
    props: {
      editable: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        module: 'round1',
        date_format: 'DD MMMM YYYY',
        }
    },
    computed: {
      ...mapGetters({
        kindMapper: 'round1/kindMapper',
        delaysMapperWithCorona: 'round1/delaysMapperWithCorona',
        delaysMapperForRegular: 'round1/delaysMapperRegular',
        delaysMapperForEarlyFlight: 'round1/delaysMapperForEarlyFlight',
        delaysMapperForDelayedWithMissedConnection: 'round1/delaysMapperDelayedWithMissedConnection',
        reasonsMapper: 'round1/reasonsMapper',
        informedEarlyFlightsMapper: 'round1/informedEarlyFlightsMapper',
        defaultCurrency: 'round1/defaultCurrency',
        delaysEarly714DaysMapper: 'round1/delaysEarly714DaysMapper',
        delaysEarly7DaysMapper: 'round1/delaysEarly7DaysMapper',
      }),
      ...mapFields([
        'delay',
        'reason',
        'people',
        'comment',
        'departure_airport',
        'arrival_airport'
      ]),
      scope() {
        return this.editable ? 'r1s1formEditable' : 'r1s1form'
      },
      reasonVisibility: function(){
        return this.kind != 'overbooked' && this.kind != 'corona' && this.kind != 'early'
      },

      showMissedConnectionFlightInput: function() {
        return this.delay == 119 && this.kind == "delayed";
      },

      selectedEarlyInformedMoreThan14DaysBefore: function() {
        return this.client_informed == "3";
      },
      selectedEarlyInformedLessThan14DaysBefore: function() {
        if (this.client_informed == "2") {console.log('SELECTED' + this.client_informed)}
        return this.client_informed == "2";
      },
      selectedEarlyInformedLessThan7DaysBefore: function() {
        if (this.client_informed == "1") {console.log('SELECTED' + this.client_informed)}
        return this.client_informed == "1";
      },
      selectedKindCorona: function() {
        return this.kind == "corona";
      },
      selectedKindRegular: function() {
        return (this.kind != 'corona' && this.kind != 'delayed' && this.kind != 'early')
      },
      selectedKindEarly: function() {
        return this.kind == 'early';
      },
      selectedKindNotEarly: function() {
        return this.kind != 'early';
      },
      selectedKindEarlyAndCompensable: function() {
        return (this.kind == 'early' && parseInt(this.client_informed) < 3)
      },
      delaysMapperDelayedWithMissedConnection: function() {
        return this.kind == 'delayed'
      },
      client_informed: {
        set: function(value) {
          this.$store.commit('round1/SET_CLIENT_INFORMED', value)
        },
        get: function() {
          return this.$store.getters['round1/getComplaintField']('client_informed')
        }
      },
      kind: {
        set: function(newVal) {
          this.$store.commit('round1/SET_KIND', newVal)
        },
        get: function() {
          return this.$store.getters['round1/getComplaintField']('kind')
        }
      },
      is_comment: {
        set: function (newVal) {
          this.$store.commit('round1/TOGGLE_COMMENT', newVal)
        },
        get: function () {
          return this.$store.getters['round1/getComplaintField']('is_comment')
        }
      },
      flight_number: {
        get: function() {
          return this.$store.getters['round1/getComplaintField']('flight_number')
        },
        set: function(newVal) {
          let correctValue = tr(newVal).toUpperCase()
          this.$store.commit('round1/UPDATE_COMPLAINT_FIELD', {
              path: 'flight_number',
              value: correctValue
          })
        }
      },

      next_flight_number: {
        get: function() {
          return this.$store.getters['round1/getComplaintField']('next_flight_number')
        },
        set: function(newVal) {
          let correctValue = newVal.toUpperCase()
          this.$store.commit('round1/UPDATE_COMPLAINT_FIELD', {
              path: 'next_flight_number',
              value: correctValue
          })
        }
      },

      isFlightNumberWarn(){
        return !/^[A-Za-z]{1}\w{1,2}\s*\d{1,4}$|^$/.test(this.flight_number)
      },

      isFlightNumberMissedConnectionWarn(){
        if (this.showMissedConnectionFlightInput) {
          return !/^[A-Za-z]{1}\w{1,2}\s*\d{1,4}$|^$/.test(this.next_flight_number)
        } else {
          return true
        }
      },

      scheduled_arrival_ui_date: {
        get: function () {
          let {date, month, year} = this.$store.getters['round1/getComplaintField']('scheduled_arrival_at_ui_date_time')
          return date ? moment(`${year}${month}${date}`).format(this.date_format) : ''
        },
        set: function (newDate) {
          let payload = {
            date: ('0' + newDate.getDate()).slice(-2),
            month: ('0' + (newDate.getMonth() + 1)).slice(-2),
            year: newDate.getFullYear()
          }
          this.$store.commit('round1/SET_SCHEDULED_ARRIVAL_AT_UI_DATE', payload)
        }
      },
    },
      mounted(){
          if (window.location.href.includes("dep_apt") || window.location.href.includes("arr_apt")  ) {
              $([document.documentElement, document.body]).animate({
                  scrollTop: $(".claim-step-wrap").offset().top
              }, 500);
          }
          // document.querySelector('.toggle').setAttribute('data-value', `${i18n.t('f_yes')} ${i18n.t('f_no')}`);
      },
    methods: {
      ...mapMutations({
        cancelEditing: 'round1/CANCEL_EDITING',
        applyEditing: 'round1/APPLY_EDITING',
        incrementPeople: 'round1/INCREMENT_PEOPLE',
        decrementPeople: 'round1/DECREMENT_PEOPLE'
      }),
      ...mapActions({
        validation: 'round1/validation',
        getCompensation: 'round1/getCompensation'
      }),
      next(){
        this.formSubmittion({scope: 'r1s1form.*', formSelector: '.r1s1form'}).then(({success, fail}) => {
          this.$store.dispatch('round1/getCompensation').then(() => {
            this.$store.commit('round1/NEXT_STEP')
            success()
          }).catch((response) => {
            fail(response)
          })
        })
      },
      cancelEditingWrap(){
        this.cancelEditing()
        this.scrollToHeader()
      },
      save(){
        this.formSubmittion({scope: 'r1s1formEditable.*', formSelector: '.r1s1form.editable'}).then(({success}) => {
          this.applyEditing()
          this.getCompensation().then(() => {
            this.$validator.validate('r1s3.*').then((isValid) => {
              if(isValid || (!isValid && this.errors.first('compensation.amount')) ){ this.scrollToHeader() }
              else{ this.scrollToInvalidField('#r1s3') }
              success()
            })
          }).catch(() => {
            success()
          })
        })
      },
      scrollToHeader() {
        let correction = (this.$device.desktop() ? 40 : 90)
        let selector = '#r1s3 .form-1'
        if (document.querySelector('#r1s3 .compensation-banner') != null) {
          selector = '#r1s3 .compensation-banner'
          correction = 100
        }
        let y = (window.cfs.offset(document.querySelector(selector)).top) - correction
        window.scrollTo({top: y, behavior: 'smooth'})
      },
      parseDate(dateString, format) {
        return moment(dateString, format).toDate()
      },
      formatDate(dateObj) {
        return dateObj
      },
      isFutureDate() {
        // accept even dates in future
        return false//date > new Date()
      },
      delayKindChanged() {
        if (this.delay != 119) {
          this.$store.commit('round1/CLEAR_MISSED_FLIGHT');
        }
      },
      setUncompensableDelay() {
        this.$store.commit('round1/SET_UNCOMPENSABLE_DELAY');
      },
    }
  }
</script>

<style lang="scss">
  @import '~@stylesheets/_vars.scss';

  .r1s1form {

    .check-btn {
      height: 100%;
      & label {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .date {
      .vdpComponent {
        width: 100%;

        &.invalid{
          input{
            border-color: $red;
          }
        }
      }

      .vdpClearInput {
        display: none;
      }

      > .icon {
        top: 42px;
        z-index: 1;
        right: 10px;
        color: gray;
        font-size: 26px;
        position: absolute;
        pointer-events: none;
      }

      .vdpHeader {
        padding: 5px 0;
        color: white;
        margin: -1em -1em 0 -1em;
        background-color: #2c2e41;
        background-image: radial-gradient(circle at 0% 50%, #494b5e, #2c2e41);

        .vdpPeriodControl > button {
          color: white;
        }

        .vdpArrow {
          height: 100%;
        }

        .vdpArrowPrev {
          &:after {
            border-right-color: white;
          }
        }

        .vdpArrowNext {
          &:after {
            border-left-color: white;
          }
        }

        select {
          margin: 0;
        }
      }

      .vdpTable {
        padding-top: 10px;

        th {
          padding: 0;
        }

        .selected {
          .vdpCellContent {
            font-weight: 400;
            background-color: $orange_a;
          }
        }

        .today {
          color: $orange_a;
          font-weight: 400;
        }
      }
    }

    .own-input{
      .content {
        // bottom: 111px;
        // top: -58px;
        height: fit-content;
      }
    }

    .own-input.plane-was{
      html.desktop &, html.tablet &{
        .content {
          // bottom: 114px;
        }
      }
    }

    .own-input.people {
      .content {
        // bottom: 89px;
      }
    }


    .message-label-wrap {
      display: flex;
      margin-bottom: 0;
      align-items: center;
      justify-content: space-between;

      .label {
        display: flex;

        .icon {
          display: block;
          margin-top: 3px;
        }
      }

      .info-tip {
        .content {
          // top: -47px !important;
          // bottom: 49px;
        }
      }
    }

    .reason-label {
      position: relative;
      margin-bottom: 10px;

      .content {
        bottom: 29px;
      }
    }

    .plane-was {
      .icon {
        margin-right: 5px;

        &.icon-cross {
          font-size: 13px;
        }
      }
    }
  }
</style>
